import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, BadgeDollarSign, ShieldCheck, Clock, Headphones, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const FeaturesSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const features = [
    { icon: Shield, titleKey: 'landing.feature1Title', descKey: 'landing.feature1Desc' },
    { icon: BadgeDollarSign, titleKey: 'landing.feature2Title', descKey: 'landing.feature2Desc' },
    { icon: ShieldCheck, titleKey: 'landing.feature3Title', descKey: 'landing.feature3Desc' },
    { icon: Headphones, titleKey: 'landing.feature4Title', descKey: 'landing.feature4Desc' },
  ];

  return (
    <section className="py-24 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('landing.whyLoveUs').split('HorseGO')[0]}<span className="text-amber-500">HorseGO</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('landing.whyDescription')}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div
                key={`feature-${feature.title || index}`}
                className="group relative bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-8 transition-all duration-500 hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/10 hover:-translate-y-1"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Icon */}
                <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber-500/20 transition-colors">
                  <IconComponent className="w-7 h-7 text-amber-500" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-amber-500 transition-colors">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {t(feature.descKey)}
                </p>

                {/* Hover indicator */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="w-5 h-5 text-amber-500" />
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={() => {
              const element = document.querySelector('#how-it-works');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-6 rounded-full text-lg transition-all duration-300 hover:scale-105"
          >
            {t('nav.howItWorks')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
